import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    campaignReportBoostUrl,
    campaignReportContentUrl,
    campaignReportCreatorsUrl,
    campaignReportSyncStatusUrl,
    campaignReportSyncUrl,
    campaignReportUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import {
    buildReportQuery,
    normalizeCampaignReportContent,
    normalizeCampaignReportCreators,
    normalizeCampaignReportSummary,
    normalizeCampaignReportSyncStatus,
    unwrapCampaignReportPayload,
} from '../../utils/campaignReportApi';
import type {
    IBoostCampaignReportPayload,
    IBoostCampaignReportResult,
    IGetCampaignReportContentPayload,
    IGetCampaignReportCreatorsPayload,
    IGetCampaignReportPayload,
    ISyncCampaignReportPayload,
} from '../../types/global/campaignReport.types';
import { CACHE_TYPE } from '../cache/types';
import { setCache } from '../cache/action';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { httpRequest } from '../types';
import {
    boostCampaignReport,
    boostCampaignReportFail,
    boostCampaignReportSuccess,
    getCampaignReport,
    getCampaignReportContent,
    getCampaignReportContentFail,
    getCampaignReportContentSuccess,
    getCampaignReportCreators,
    getCampaignReportCreatorsFail,
    getCampaignReportCreatorsSuccess,
    getCampaignReportFail,
    getCampaignReportSuccess,
    getCampaignReportSyncStatus,
    getCampaignReportSyncStatusFail,
    getCampaignReportSyncStatusSuccess,
    syncCampaignReport,
    syncCampaignReportFail,
    syncCampaignReportSuccess,
} from './actions';

type SagaAction<T = unknown> = { payload: T; resolve?: (v: unknown) => void; reject?: (e: unknown) => void };

function reportCacheKey(campaignId: string, suffix: string): string {
    return `${campaignId}:${suffix}`;
}

function reportErrorMessage(error: unknown, fallback: string): string {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return err?.response?.data?.message || err?.message || fallback;
}

function* getCampaignReportSaga({ payload }: SagaAction<IGetCampaignReportPayload>): Generator<any, void, any> {
    const window = payload.window ?? 'all_time';
    const key = reportCacheKey(payload.campaignId, `summary:${window}`);
    const useCache = payload.skipCache ? false : yield* isUseCacheEnabled();
    const cached = useCache ? yield* getNotUniqueCacheByKey(CACHE_TYPE.CAMPAIGN_REPORT, key) : null;

    if (cached?.value) {
        yield put(getCampaignReportSuccess(cached.value));
        return;
    }

    try {
        const qs = buildReportQuery({ window });
        const response = yield call(api, `${campaignReportUrl(payload.campaignId)}${qs}`, httpRequest.GET, null, 2);
        const data = normalizeCampaignReportSummary(response);
        yield put(getCampaignReportSuccess(data));
        yield put(setCache({ key, value: data, type: CACHE_TYPE.CAMPAIGN_REPORT, isUnique: false }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCampaignReportFail(error));
        yield call(showToastMessage, reportErrorMessage(error, 'Failed to load campaign report'), 'error');
    }
}

function* getCampaignReportContentSaga({
    payload,
}: SagaAction<IGetCampaignReportContentPayload>): Generator<any, void, any> {
    const window = payload.window ?? 'all_time';
    const sort = payload.sort ?? 'impact_score';
    const order = payload.order ?? 'desc';
    const page = payload.page ?? 1;
    const limit = payload.limit ?? 20;
    const platform = payload.platform ?? 'all';
    const search = payload.search ?? '';
    const key = reportCacheKey(
        payload.campaignId,
        `content:${window}:${platform}:${sort}:${order}:${page}:${limit}:${search}`,
    );
    const useCache = payload.skipCache ? false : yield* isUseCacheEnabled();
    const cached = useCache ? yield* getNotUniqueCacheByKey(CACHE_TYPE.CAMPAIGN_REPORT, key) : null;

    if (cached?.value) {
        yield put(getCampaignReportContentSuccess(cached.value));
        return;
    }

    try {
        const qs = buildReportQuery({
            window,
            platform: payload.platform,
            search: payload.search,
            sort,
            order,
            page,
            limit,
        });
        const response = yield call(
            api,
            `${campaignReportContentUrl(payload.campaignId)}${qs}`,
            httpRequest.GET,
            null,
            2,
        );
        const data = normalizeCampaignReportContent(response);
        yield put(getCampaignReportContentSuccess(data));
        yield put(setCache({ key, value: data, type: CACHE_TYPE.CAMPAIGN_REPORT, isUnique: false }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCampaignReportContentFail(error));
        yield call(showToastMessage, reportErrorMessage(error, 'Failed to load report content'), 'error');
    }
}

function* getCampaignReportCreatorsSaga({
    payload,
}: SagaAction<IGetCampaignReportCreatorsPayload>): Generator<any, void, any> {
    const window = payload.window ?? 'all_time';
    const sort = payload.sort ?? 'impact_score';
    const page = payload.page ?? 1;
    const limit = payload.limit ?? 20;
    const key = reportCacheKey(payload.campaignId, `creators:${window}:${sort}:${page}:${limit}`);
    const useCache = payload.skipCache ? false : yield* isUseCacheEnabled();
    const cached = useCache ? yield* getNotUniqueCacheByKey(CACHE_TYPE.CAMPAIGN_REPORT, key) : null;

    if (cached?.value) {
        yield put(getCampaignReportCreatorsSuccess(cached.value));
        return;
    }

    try {
        const qs = buildReportQuery({ window, sort, page, limit });
        const response = yield call(
            api,
            `${campaignReportCreatorsUrl(payload.campaignId)}${qs}`,
            httpRequest.GET,
            null,
            2,
        );
        const data = normalizeCampaignReportCreators(response);
        yield put(getCampaignReportCreatorsSuccess(data));
        yield put(setCache({ key, value: data, type: CACHE_TYPE.CAMPAIGN_REPORT, isUnique: false }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCampaignReportCreatorsFail(error));
        yield call(showToastMessage, reportErrorMessage(error, 'Failed to load creator leaderboard'), 'error');
    }
}

function* getCampaignReportSyncStatusSaga({
    payload,
}: SagaAction<{ campaignId: string; skipCache?: boolean }>): Generator<any, void, any> {
    const key = reportCacheKey(payload.campaignId, 'sync-status');
    const useCache = payload.skipCache ? false : yield* isUseCacheEnabled();
    const cached = useCache ? yield* getNotUniqueCacheByKey(CACHE_TYPE.CAMPAIGN_REPORT, key) : null;

    if (cached?.value) {
        yield put(getCampaignReportSyncStatusSuccess(cached.value));
        return;
    }

    try {
        const response = yield call(api, campaignReportSyncStatusUrl(payload.campaignId), httpRequest.GET, null, 2);
        const data = normalizeCampaignReportSyncStatus(response);
        yield put(getCampaignReportSyncStatusSuccess(data));
        yield put(setCache({ key, value: data, type: CACHE_TYPE.CAMPAIGN_REPORT, isUnique: false }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getCampaignReportSyncStatusFail(error));
    }
}

function* syncCampaignReportSaga({
    payload,
    resolve,
    reject,
}: SagaAction<ISyncCampaignReportPayload>): Generator<any, void, any> {
    try {
        yield call(api, campaignReportSyncUrl(payload.campaignId), httpRequest.POST, {}, 2);
        yield put(syncCampaignReportSuccess({ campaignId: payload.campaignId }));
        yield call(showToastMessage, 'Metric sync queued', 'success');
        yield put(getCampaignReportSyncStatus({ campaignId: payload.campaignId, skipCache: true }));
        resolve?.(true);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(syncCampaignReportFail(error));
        yield call(showToastMessage, reportErrorMessage(error, 'Failed to queue metric sync'), 'error');
        reject?.(error);
    }
}

function* boostCampaignReportSaga({
    payload,
    resolve,
    reject,
}: SagaAction<IBoostCampaignReportPayload>): Generator<any, void, any> {
    const { campaignId, ...body } = payload;
    try {
        const response = yield call(api, campaignReportBoostUrl(campaignId), httpRequest.POST, body, 2);
        const data = unwrapCampaignReportPayload<IBoostCampaignReportResult>(response);
        yield put(boostCampaignReportSuccess(data));
        yield call(showToastMessage, 'Boost draft created', 'success');
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(boostCampaignReportFail(error));
        yield call(showToastMessage, reportErrorMessage(error, 'Failed to create boost draft'), 'error');
        reject?.(error);
    }
}

export function* campaignReportWatchers(): Generator<any, void, any> {
    yield all([
        takeLatest(getCampaignReport, getCampaignReportSaga),
        takeLatest(getCampaignReportContent, getCampaignReportContentSaga),
        takeLatest(getCampaignReportCreators, getCampaignReportCreatorsSaga),
        takeLatest(getCampaignReportSyncStatus, getCampaignReportSyncStatusSaga),
        takeLatest(syncCampaignReport, syncCampaignReportSaga),
        takeLatest(boostCampaignReport, boostCampaignReportSaga),
    ]);
}
